<script>
import findIndex from 'lodash/findIndex';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ServiceTimeModel from '@models/ServiceTime';
import ReportModel from '@models/Report';
import ServiceTimeForm from '@views/reports/service-time-form';
import ServiceTimeModal from '@views/reports/edit-service-time';

export default {
  components: {
    FontAwesomeIcon,
    ServiceTimeForm,
    ServiceTimeModal,
  },
  props: {
    collapsed: {
      default: false,
      type: Boolean,
      required: false,
    },
    // eslint-disable-next-line
    reportId: {
      required: true,
      default: null,
    },
    carUses: {
      default: () => [],
      type: Array,
    },
    serviceTimes: {
      default: () => [],
      type: Array,
    },
    locked: {
      type: Number,
      default: 0,
    },
    completed: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedServiceTime: null,
      fields: [
        {
          key: 'car_use',
          label: this.$t('fields.car_use'),
          sortable: false,
        },
        {
          key: 'date',
          label: this.$t('fields.date'),
          sortable: true,
          formatter: (value) => {
            if (value) {
              return DateTime.fromSQL(value).toFormat('dd.MM.yyyy');
            } else {
              return '-';
            }
          },
        },
        {
          key: 'approach_start',
          label: this.$t('fields.approach_start'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        {
          key: 'approach_end',
          label: this.$t('fields.approach_end'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        {
          key: 'service_start',
          label: this.$t('fields.service_start'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        {
          key: 'service_end',
          label: this.$t('fields.service_end'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        {
          key: 'return_start',
          label: this.$t('fields.return_start'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        {
          key: 'return_end',
          label: this.$t('fields.return_end'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        {
          key: 'idle_time',
          label: this.$t('fields.idle_time'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        // km_driven: {
        //   label: this.$t('fields.km_driven'),
        //   sortable: true,
        // },
        {
          key: 'total_approach',
          label: this.$t('table_labels.total_approach'),
          sortable: false,
        },
        {
          key: 'total_service',
          label: this.$t('table_labels.total_service'),
          sortable: false,
        },
        {
          key: 'total_return',
          label: this.$t('table_labels.total_return'),
          sortable: false,
        },
        {
          key: 'total_time',
          label: this.$t('table_labels.total_time'),
          sortable: false,
        },
        {
          key: 'completed',
          label: this.$t('fields.status'),
          sortable: false,
        },
      ],
    };
  },
  mounted() {
    if (!this.locked && !this.completed) {
      this.fields.push({
        key: 'actions',
        label: this.$t('table_labels.actions'),
        sortable: false,
      });
    }
  },
  methods: {
    submit(serviceTime) {
      this.$emit('submitted', serviceTime);
    },
    update(serviceTime) {
      this.selectedServiceTime = null;
      const key = findIndex(this.serviceTimes, { id: serviceTime.id });
      this.$emit('updated', serviceTime, key);
      if (!serviceTime.completed) this.$store.dispatch('common/updateCurrentReportStatus', 0);
    },
    async deleteServiceTime(item) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.service_time') }))) {
        this.$wait.start(`serviceTime${item.id}`);
        const key = findIndex(this.serviceTimes, { id: item.id });
        const report = new ReportModel({ id: this.reportId });
        const serviceTime = new ServiceTimeModel({ id: item.id });
        await serviceTime.for(report).delete();
        this.$emit('deleted', item.id, key);
        this.$wait.end(`serviceTime${item.id}`);
      }
    },
    async duplicate(item) {
      if (confirm(this.$t('alerts.duplicate_entity', { entity: this.$t('entities.service_time') }))) {
        this.$wait.start('service-time processing');
        const report = new ReportModel({ id: this.reportId });
        const serviceTime = new ServiceTimeModel({
          id: `duplicate/${item.id}`,
        });

        serviceTime
          .for(report)
          .save()
          .then((response) => {
            this.$wait.end('service-time processing');
            this.$emit('submitted', response.service_time);
          })
          .catch(() => {
            this.$wait.end('service-time processing');
          });
      }
    },
    timeFormatter(value) {
      return value ? DateTime.fromSQL(value).toFormat('HH:mm') : ' - ';
    },
  },
};
</script>

<template>
  <div>
    <b-collapse id="service-times-collapse" v-model="collapsed" class="mt-2">
      <ServiceTimeForm v-if="!locked && !completed" ref="serviceTimeForm" :car-uses="carUses" :report-id="reportId" @submitted="submit" />
    </b-collapse>
    <small v-if="serviceTimes.length" class="table-responsive">
      <br />
      <b-table stacked="lg" responsive striped hover :items="serviceTimes" :fields="fields" small table-class="no-min-width">
        <template v-slot:cell(car_use)="row">
          <div v-if="row.item.car_use.car"> {{ row.item.car_use.car.license_plate }} </div>
          <div v-else> - </div>
        </template>
        <template v-slot:cell(date)="data">
          <div>{{ data.value }}</div>
        </template>
        <template v-slot:cell(total_approach)="row">
          <div v-if="row.item.total_approach_time" class="no-wrap">{{ row.item.total_approach_time }}</div>
          <div v-else>-</div>
        </template>
        <template v-slot:cell(total_service)="row">
          <div v-if="row.item.total_service_time" class="no-wrap">{{ row.item.total_service_time }}</div>
          <div v-else>-</div>
        </template>
        <template v-slot:cell(total_return)="row">
          <div v-if="row.item.total_return_time" class="no-wrap">{{ row.item.total_return_time }}</div>
          <div v-else>-</div>
        </template>
        <template v-slot:cell(total_time)="row">
          <div v-if="row.item.total_time" class="no-wrap">{{ row.item.total_time }}</div>
          <div v-else>-</div>
        </template>
        <template v-slot:cell(completed)="row">
          <FontAwesomeIcon v-if="row.item.completed" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn
              :disabled="$wait.is(`serviceTime${row.item.id}`)"
              class="mr-2"
              size="sm"
              variant="outline-primary"
              @click="selectedServiceTime = row.item.id"
            >
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn :disabled="$wait.is('service-time processing')" class="mr-2" size="sm" variant="outline-success" @click="duplicate(row.item)">
              <FontAwesomeIcon :icon="['fas', 'copy']" />
            </b-btn>
            <b-btn :disabled="$wait.is(`serviceTime${row.item.id}`)" class="mr-2" size="sm" variant="outline-danger" @click="deleteServiceTime(row.item)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:row-details="row">
          <b-card>
            {{ row.item.notes }}
          </b-card>
        </template>
      </b-table>
    </small>

    <ServiceTimeModal
      v-if="!locked && !completed"
      v-model="selectedServiceTime"
      :car-uses="carUses"
      :loaded-id="selectedServiceTime"
      :report-id="reportId"
      @updated="update"
      @hidden="selectedServiceTime = null"
    />
  </div>
</template>
